import { useMemo } from "react";

import type {
    AppsSdksSignalColumnName,
    AppsSdksSignalItemDto,
} from "app/pages/sales-intelligence/domain/signals/types/apps-signal";
import type { IBaseAppsRowDto } from "app/pages/sales-intelligence/modules/apps-search/types/rows";
import { appOsToStoreMap } from "app/pages/sales-intelligence/modules/apps-search-filters/constants";
import solutionUrlsService from "app/pages/sales-intelligence/services/solution-urls";
import {
    createDateColumn,
    createSdkNameColumn,
} from "app/pages/sales-intelligence/sub-modules/signals/services/table-columns/apps/tooltipColumns";

import { useTrackedSignalTooltip } from "../../../hooks/cells/useTrackedSignalTooltip";
import { SignalsSdksTooltipContainer } from "../SignalsSdksTooltipContainer/SignalsSdksTooltipContainer";

import { SdksCellText } from "./SdksCellText";

type Props = {
    field: AppsSdksSignalColumnName;
    record: IBaseAppsRowDto;
};

/**
 * Apps surface. The row already names its own app, so the tooltip drops that column
 * and offers the App Intelligence link instead.
 */
export const AppsSdksCellContainer = ({ field, record }: Props) => {
    const value = (record[field] ?? []) as readonly AppsSdksSignalItemDto[];
    const { onTrackedTooltipClick } = useTrackedSignalTooltip({ field, value: value.length });

    const columns = useMemo(() => [createSdkNameColumn(), createDateColumn(field)], [field]);

    // Every SDK row here belongs to the table row's own app, so any of them resolves the same link.
    const [firstSdk] = value;
    const redirectLink =
        firstSdk && solutionUrlsService.getAppTechnographicsUrl(firstSdk.appId, appOsToStoreMap[firstSdk.market]);

    return (
        <SignalsSdksTooltipContainer
            field={field}
            value={value}
            columns={columns}
            redirectLink={redirectLink}
            onViewAll={onTrackedTooltipClick}
        >
            <SdksCellText value={value} />
        </SignalsSdksTooltipContainer>
    );
};
